import { Store } from 'vuex';
import { AxiosInstance, AxiosResponse, AxiosError } from 'axios';

import { AppState } from '@/store';
import { MUTATIONS } from '@/store/order';
import {
  Order,
  OrderWithContext,
  OrderMealRequest,
  CustomerDeliveryAddress,
  SubmitOrderRequest,
  OrderStatus,
  OrderType,
  OrderMeal,
  UpdateDeliveryInfoOnOrderRequest,
  OrderMealItem,
  OrderNotFoundException,
  DeliveryEstimate,
  OrderErrorCode,
  OrderMealEntree,
  OrderApiException,
  Promotion,
  UndeliverableItem,
  OrderMealEntreeContents,
  CreateDonationApiRequest,
  NonFoodItemRequest,
  RecentOrFavoriteOrderMeal,
  GetDeliveryEstimateRequest,
  DeliveryAddressValidationRequest,
  DeliveryApiException,
  DeliveryApiValidationError,
  DeliveryAddressValidationResponse,
  PromoOnlyErrorTypes,
  FACPromoOptions,
  PromoErrorParams,
  Restaurant,
  CustomerProfile,
  OrderApiError,
  ApiValidationError,
  DeliveryApiError,
  DeliveryErrorCode
} from '@/models';
import { ApiService } from '../api/api.service';
import { Logger } from '../logger/logger';
import {
  updateOrderWithApiResponseEtag,
  generateOrderApiExceptionAndThrow,
  convertOrderMealToCreateMealRequest,
  fromDeliveryAddressToUpdateDeliveryInfoRequest,
  generateOrderApiErrors,
  convertRecentAndFavsMealToCreateMealRequest,
} from './order-helper';
import { PromotionsService } from '../promotions/promotions.service';
import { arrayToMap, sleep, isUserLoyaltyEligible } from '@/helpers';
import { UiService } from '../ui/ui.service';
import { PaymentService } from '../payment/payment.service';
import { BagService } from '../bag/bag.service';
import { AnalyticsEventAction, AnalyticsEventCategory, AnalyticsEventLabel, GoogleAnalyticsService } from '../google/google-analytics.services';
import isEqual from 'lodash/isEqual';
import { AnalyticsService } from '../analytics/analytics.service';
import { ErrorEvent } from '../analytics/events';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import router, { Routes } from '@/router';
import { RetrievalType } from '../analytics/platforms/adobe-analytics/constants';
import { ApiError } from '../analytics/platforms/adobe-analytics/models';

export interface OrderService {
  showPickUpOnlyPromoErrorModal(): void;
  revertBackToCheckOutPickUp(): Promise<void>;
  getLastOrderedRestaurantId(): Promise<number>;
  changeRestaurantForOrder(order: OrderWithContext, restaurantId: number, forPickup: boolean): Promise<void>;

  /** handles logic to switch between personal/regular and group orders */
  getCurrentOrder(): OrderWithContext | null;
  processOrderDiscountErrors(order: OrderWithContext, promoCode: string): void;
  doesInProgressHostGroupOrderExist(): Promise<boolean>;
  isCustomerHostOfGroupOrder(): boolean;
  isCustomerParticipantOfGroupOrder(): boolean;
  setIsCustomerHostOfGroupOrder(value: boolean): void;
  groupParticpantHasMeal(mealIds: string[]): boolean;
  clearGroupParticipantMeal(): void;
  removeAllDiscounts(): void;
  getOrder(orderId: string, finalizePricing?: boolean): Promise<OrderWithContext | null>;
  getGroupOrder(groupOrderId: string, finalizePricing?: boolean): Promise<OrderWithContext | null>;

  pollGroupOrderIfExists(): Promise<void>;
  stopPollingGroupOrder(): void;

  createOrder(restaurantId: number, orderType?: OrderType, groupOrderMessage?: string): Promise<OrderWithContext | null>;
  createGroupOrder(restaurantId: number, groupOrderMessage: string): Promise<OrderWithContext | null>;
  cancelOrder(orderId: string, orderType?: OrderType): Promise<void>;

  createDonation(order: OrderWithContext, request: CreateDonationApiRequest): Promise<void>;
  deleteDonation(order: OrderWithContext, campaignId: string): Promise<void>;

  isPickingUp(): boolean;
  isDelivery(): boolean;
  switchToPickup(): void;

  refreshCurrentOrder(finalizePricing?: boolean): Promise<void>;
  refreshPendingOrders(finalizePricing?: boolean): Promise<void>;
  refreshOrder(localOrder: OrderWithContext | null, finalizePricing: boolean): Promise<OrderWithContext | null>;

  setPendingOrder(order: OrderWithContext | null): void;
  setPendingGroupOrder(order: OrderWithContext | null, participantName: string): void;
  setIsGroupOrderParticipant(isGroupOrderParticipant: boolean): void;
  convertOrderToGroupOrder(order: OrderWithContext): Promise<OrderWithContext>;
  setUpsellMeal(meal: OrderMeal | null): void;

  resetCurrentOrder(): void;
  resetPendingOrder(): void;
  resetPendingGroupOrder(): void;
  resetPendingOrders(): void;

  updateMealName(currentOrder: OrderWithContext, mealId: string, mealName: string): Promise<void>;
  addMealToOrder(order: OrderWithContext, request: OrderMealRequest): Promise<string>;
  addPreconfiguredMealToOrder(order: OrderWithContext, preconfiguredMealId: string, mealName: string): Promise<void>;
  addNonFoodItemToOrder(order: OrderWithContext, request: NonFoodItemRequest): Promise<void>;
  removeNonFoodItemFromOrder(order: OrderWithContext, nonFoodItemId: string): Promise<void>;
  removeMealFromOrder(order: OrderWithContext, mealId: string): Promise<void>;
  updateMealInOrder(order: OrderWithContext, mealId: string, request: OrderMealRequest): Promise<void>;
  duplicateMeal(order: OrderWithContext, meal: OrderMeal): Promise<void>;
  duplicateRecentsAndFavsMeal(order: OrderWithContext, meal: RecentOrFavoriteOrderMeal): Promise<void>;
  copyMeals(fromId: string, to: OrderWithContext): Promise<void>;

  updateOrderTip(order: OrderWithContext, tip: number): Promise<void>;
  updateOrderCrewTip(order: OrderWithContext, tip: number): Promise<void>;
  applyDiscountToOrder(order: OrderWithContext, promotionCode: string): Promise<void>;
  removeDiscountFromOrder(currentOrder: OrderWithContext, promotionCode: string): Promise<void>;

  setDeliveryAddress(newAddress: CustomerDeliveryAddress | null): Promise<void>;
  changeCustomerOnOrder(order: OrderWithContext, jwt: string): Promise<void>;
  setOrderPhoneNumber(phoneNumber: string): void;

  submitOrder(order: OrderWithContext, request: SubmitOrderRequest): Promise<void>;
  setLastOrder(order: OrderWithContext | null, estimate: DeliveryEstimate | null): void;
  setPromoCode(promoCode: string): void;
  setPromoErrorMessage(promoErrorMessage: string): void;
  revertBackToCheckOutDelivery(): void;

  addDeliveryInfoToOrder(order: OrderWithContext, deliveryInfo: UpdateDeliveryInfoOnOrderRequest): Promise<void>;
  updateDeliveryDropoffInfoOnOrder(order: OrderWithContext, deliveryInfo: UpdateDeliveryInfoOnOrderRequest): Promise<void>;
  removeDeliveryInfoFromOrder(order: OrderWithContext): Promise<any>;

  removeItemsFromOrder(order: OrderWithContext, items: UndeliverableItem[]): Promise<void>;

  getDeliveryEstimate(request: GetDeliveryEstimateRequest): Promise<DeliveryEstimate>;
  validateDeliveryAddress(address: DeliveryAddressValidationRequest): Promise<DeliveryAddressValidationResponse | DeliveryApiException>;
  updateCustomerLoyaltyEnrollmentStatus(order: OrderWithContext, jwt: string): Promise<void>;
  showDeliveryOnlyPromoErrorModal(): void;
  makeFACPromoOptions(options: 'pickup' | 'delivery'): FACPromoOptions;
  setCachedDeliveryAddress(newAddress: CustomerDeliveryAddress | null): void;
  setCachedRestaurant(restaurant: Restaurant | null): void;
}

export const ONLINE_ORDERS_BASE_ROUTE = '/order/v2/online';
export const CUSTOMERS_BASE_ROUTE = '/order/v2/customers';
export const DELIVERIES_BASE_ROUTE = '/order/v2/delivery';

// This is to differentiate between ios, android, alexa, etc. 'WebV2' was copied from the previous web app, as required by the BI team.
export const ORDER_SOURCE = 'WebV2';
export const GROUP_ORDER_MAX_PARTICIPANTS = 20;
export const ORDER_EMBED = 'order';
const NUM_MAX_ORDER_OP_RETRIES = 1;
const GROUP_POLL_DELTA_MS = 5000;

export class AppOrderService extends ApiService implements OrderService {
  shouldPollGroupOrder = false;
  // Prevents multiple polling coroutines from existing simultaneously
  isPolling = false;

  constructor(
    private payment: PaymentService,
    private promotions: PromotionsService,
    private ui: UiService,
    private bag: BagService,
    private googleAnalytics: GoogleAnalyticsService,
    protected analytics: AnalyticsService,
    httpClient: AxiosInstance,
    store: Store<AppState>,
    logger: Logger,
  ) {
    super(store, httpClient, logger, analytics);
  }

  get cachedRestaurant(): Restaurant {
    return this.store.getters.cachedRestaurant;
  }

  get currentOrder(): OrderWithContext {
    return this.store.getters.currentOrder;
  }

  get customer(): CustomerProfile | null {
    const profile = this.store.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  isLoyaltyEligible() {
    if (!this.customer || this.customer.isGuest) {
      return false;
    }
    return isUserLoyaltyEligible(this.store.state.ui.countryProfile.countryCode, this.customer.country || 'US');
  } 

  setCachedRestaurant(restaurant: Restaurant | null) {
    this.store.commit(MUTATIONS.SET_CACHED_RESTAURANT, restaurant);
  }

  async refreshPendingOrders(finalizePricing: boolean = true): Promise<void> {
    await Promise.all([this.refreshOrder(this.store.state.order.pendingOrder, finalizePricing), this.refreshOrder(this.store.state.order.pendingGroupOrder, finalizePricing)]);
  }

  async refreshCurrentOrder(finalizePricing: boolean = true): Promise<void> {
    await this.refreshOrder(this.currentOrder, finalizePricing);
  }

  getCachedRestaurant(): Restaurant | null {
    return this.cachedRestaurant;
  }

  getCurrentOrder(): OrderWithContext | null {
    return this.currentOrder;
  }

  async pollGroupOrderIfExists(): Promise<void> {
    if (!this.store.state.order.pendingGroupOrder) {
      return;
    }
    this.shouldPollGroupOrder = true;

    if (this.isPolling) {
      return;
    }
    while (this.shouldPollGroupOrder) {
      this.isPolling = true;
      const groupOrder = await this.refreshOrder(this.store.state.order.pendingGroupOrder, true);
      this.store.commit(MUTATIONS.SET_GROUP_ORDER, groupOrder);
      await sleep(GROUP_POLL_DELTA_MS);
    }
    this.isPolling = false;
  }

  stopPollingGroupOrder() {
    this.shouldPollGroupOrder = false;
  }

  async getOrder(orderId: string, finalizePricing: boolean = true): Promise<OrderWithContext | null> {
    return this.getOrderData(`${ONLINE_ORDERS_BASE_ROUTE}/${orderId}`, finalizePricing && this.payment.isPaymentOnline());
  }

  /** This does NOT calculate tax. If a customer is currently a host of a Group Order, use the getOrder() method above */
  async getGroupOrder(groupOrderId: string, finalizePricing: boolean = true): Promise<OrderWithContext | null> {
    return this.getOrderData(`${ONLINE_ORDERS_BASE_ROUTE}/group/${groupOrderId}`, finalizePricing && this.payment.isPaymentOnline());
  }

  async createOrder(restaurantId: number, orderType = OrderType.Regular, groupOrderMessage?: string): Promise<OrderWithContext | null> {
    // reset any existing stored order
    this.resetOrder(orderType);

    const orderWithContext = await this.httpClient
      .post(ONLINE_ORDERS_BASE_ROUTE, { restaurantId, orderType, groupOrderMessage, orderSource: ORDER_SOURCE }, { params: { embeds: ORDER_EMBED } })
      .then((r: AxiosResponse<{ order: Order; }>) => ({ etag: r.headers['etag'], order: r.data.order, discounts: null }))
      .catch(e => this.logAndThrowApiError('createOrder', e));

    const deliveryAddress = this.store.state.order.selectedDeliveryAddress;
    if (orderWithContext && this.isDelivery() && deliveryAddress) {
      await this.addDeliveryInfoToOrder(orderWithContext, fromDeliveryAddressToUpdateDeliveryInfoRequest(deliveryAddress));
    }

    this.refreshDiscountsAndSetOrder(null, orderWithContext);

    return orderWithContext;
  }

  async createGroupOrder(restaurantId: number, message: string): Promise<OrderWithContext | null> {
    return this.createOrder(restaurantId, OrderType.Group, message);
  }

  async getLastOrderedRestaurantId(): Promise<number> {
    return this.httpClient
      .get(`${CUSTOMERS_BASE_ROUTE}/orders/recents/lastrestaurantorderedfrom`)
      .then((r: AxiosResponse) => r.data.restaurantId)
      .catch(e => this.logAndThrowApiError('getLastOrderedRestaurantId', e));
  }

  async cancelOrder(orderId: string, orderType: OrderType = OrderType.Regular): Promise<void> {
    this.setUpsellMeal(null);
    return this.httpClient
      .delete(`${ONLINE_ORDERS_BASE_ROUTE}/${orderId}`)
      .then((r: AxiosResponse) => r.data)
      .then(() => this.resetOrder(orderType))
      .catch(e => this.logAndThrowApiError('cancelOrder', e));
  }

  async createDonation(order: OrderWithContext, request: CreateDonationApiRequest): Promise<void> {
    return this.httpClient
      .post(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/donations`, request, {
        headers: { 'If-Match': order.etag },
        params: { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((r: AxiosResponse) => this.handleOrderResponse(order, r.data, r))
      .catch(e => this.logAndThrowApiError('createDonation', e));
  }

  async deleteDonation(order: OrderWithContext, campaignId: string): Promise<void> {
    return this.httpClient
      .delete(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/donations/${campaignId}`, {
        headers: { 'If-Match': order.etag },
        params: { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((r: AxiosResponse) => this.handleOrderResponse(order, r.data, r))
      .catch(e => this.logAndThrowApiError('deleteDonation', e));
  }

  isPickingUp() {
    return !this.store.state.order.selectedDeliveryAddress;
  }

  isDelivery() {
    return !this.isPickingUp();
  }

  async switchToPickup() {
    const deilveryRemovalResult = this.setDeliveryAddress(null);

    deilveryRemovalResult.then(r => {
      const order = this.store.state.order.pendingOrder || this.store.state.order.pendingGroupOrder || null;
      if (order && !this.store.state.restaurant.selectedRestaurant?.crewTipPickupEnabled && this.store.state.featureFlags.orderingStatus?.isCrewTippingEnabled) {
        this.updateOrderCrewTip(order, 0.00);
      }
    });
  }

  async doesInProgressHostGroupOrderExist(): Promise<boolean> {
    if (!this.store.state.order.pendingGroupOrder || !this.store.state.order.isGroupOrderHost) {
      return false;
    }

    try {
      // Get the most up-to-date info about the group order to make sure it's still valid
      const order = await this.getOrder(this.store.state.order.pendingGroupOrder.order.orderId);
      return order != null && order.order.orderStatus == OrderStatus.InProcess;
    } catch (error) {
      if (this.isAxiosErrorWithStatusCode(error, 404)) {
        return false;
      }

      throw error;
    }
  }

  async setDeliveryAddress(newAddress: CustomerDeliveryAddress | null) {
    const formattedAddress = newAddress;

    if (formattedAddress) {
      formattedAddress.latitude = parseFloat(formattedAddress.latitude as any);
      formattedAddress.longitude = parseFloat(formattedAddress.longitude as any);
    }

    if (this.isDelivery()) {
      this.setLastRetrievalTypeInStore(RetrievalType.Delivery);
    } else if(!this.isDelivery() && this.store.state.restaurant.selectedRestaurant) {
      this.setLastRetrievalTypeInStore(RetrievalType.Pickup);
    }

    this.store.commit(MUTATIONS.SET_SELECTED_DELIVERY_ADDRESS, formattedAddress);
    if (formattedAddress) {
      this.setCachedDeliveryAddress(formattedAddress);
    }

    // If we need to set any delivery info on the order, figure it out here:
    if (this.currentOrder) {
      if (!formattedAddress) {
        await this.removeDeliveryInfoFromOrder(this.currentOrder);
      } else {
        const addressRequest = fromDeliveryAddressToUpdateDeliveryInfoRequest(formattedAddress);
        if (this.currentOrder.order.delivery) {
          await this.updateDeliveryDropoffInfoOnOrder(this.currentOrder, addressRequest);
        } else {
          await this.addDeliveryInfoToOrder(this.currentOrder, addressRequest);
        }
      }
    }
  }

  setCachedDeliveryAddress(newAddress: CustomerDeliveryAddress | null) {
    const formattedAddress = newAddress;

    if (formattedAddress) {
      formattedAddress.latitude = parseFloat(formattedAddress.latitude as any);
      formattedAddress.longitude = parseFloat(formattedAddress.longitude as any);
    }

    this.store.commit(MUTATIONS.SET_CACHED_DELIVERY_ADDRESS, formattedAddress);
  }

  setOrderPhoneNumber(phoneNumber: string) {
    this.store.commit(MUTATIONS.SET_ORDER_PHONE_NUMBER, phoneNumber);
  }

  resetCurrentOrder(): void {
    if (!this.currentOrder) {
      return;
    }

    this.setUpsellMeal(null);
    if (this.currentOrder.order.orderType == OrderType.Regular) {
      this.setPendingOrder(null);
    } else {
      this.setPendingGroupOrder(null, '');
      this.setIsCustomerHostOfGroupOrder(false);
    }
  }

  async addMealToOrder(order: OrderWithContext, request: OrderMealRequest): Promise<string> {
    const isOrderForGroupParticipant = order.order.orderType == OrderType.Group && !this.isCustomerHostOfGroupOrder();
    const url = isOrderForGroupParticipant
      ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/meals`
      : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/meals`;

    const mealId = await this.performOrderOpAndRetryIfStale(
      order,
      () => this.addMealToOrderWithUrl(url, order, request, isOrderForGroupParticipant),
      refreshedOrder => this.addMealToOrderWithUrl(url, refreshedOrder, request, isOrderForGroupParticipant),
    );

    return mealId;
  }

  async addPreconfiguredMealToOrder(order: OrderWithContext, preconfiguredMealId: string, mealName: string): Promise<void> {
    const isOrderForGroupParticipant = order.order.orderType == OrderType.Group && !this.isCustomerHostOfGroupOrder();

    const url = isOrderForGroupParticipant
      ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/preconfiguredmeals`
      : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/preconfiguredmeals`;

    await this.performOrderOpAndRetryIfStale(
      order,
      () => this.addPreconfiguredMealToOrderWithUrl(url, order, preconfiguredMealId, mealName, isOrderForGroupParticipant),
      refreshedOrder => this.addPreconfiguredMealToOrderWithUrl(url, refreshedOrder, preconfiguredMealId, mealName, isOrderForGroupParticipant),
    );
  }

  async addNonFoodItemToOrder(order: OrderWithContext, request: NonFoodItemRequest): Promise<void> {
    const isOrderForGroupParticipant = order.order.orderType == OrderType.Group && !this.isCustomerHostOfGroupOrder();
    const url = isOrderForGroupParticipant
      ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/nonFoodItems`
      : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/nonFoodItems`;

    const itemId = await this.performOrderOpAndRetryIfStale(
      order,
      () => this.addNonFoodItemToOrderWithUrl(url, order, request, isOrderForGroupParticipant),
      refreshedOrder => this.addNonFoodItemToOrderWithUrl(url, refreshedOrder, request, isOrderForGroupParticipant),
    );

    return itemId;
  }

  async removeMealFromOrder(order: OrderWithContext, mealId: string): Promise<void> {
    const isOrderForGroupParticipant = this.isCustomerParticipantOfGroupOrder();
    const url = isOrderForGroupParticipant
      ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/meals/${mealId}`
      : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/meals/${mealId}`;

    await this.performOrderOpAndRetryIfStale(
      order,
      () => this.removeMealFromOrderWithUrl(url, order, isOrderForGroupParticipant),
      refreshedOrder => this.removeMealFromOrderWithUrl(url, refreshedOrder, isOrderForGroupParticipant),
    );
  }

  async removeNonFoodItemFromOrder(order: OrderWithContext, nonFoodItemId: string): Promise<void> {
    const isOrderForGroupParticipant = this.isCustomerParticipantOfGroupOrder();
    const url = isOrderForGroupParticipant
      ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/nonFoodItems/${nonFoodItemId}`
      : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/nonFoodItems/${nonFoodItemId}`;

    await this.performOrderOpAndRetryIfStale(
      order,
      () => this.removeNonFoodItemFromOrderWithUrl(url, order, isOrderForGroupParticipant),
      refreshedOrder => this.removeNonFoodItemFromOrderWithUrl(url, refreshedOrder, isOrderForGroupParticipant),
    );
  }

  async updateMealInOrder(order: OrderWithContext, mealId: string, request: OrderMealRequest): Promise<void> {
    const isOrderForGroupParticipant = this.isCustomerParticipantOfGroupOrder();
    const url = isOrderForGroupParticipant
      ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/meals/${mealId}`
      : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/meals/${mealId}`;

    await this.performOrderOpAndRetryIfStale(
      order,
      () => this.updateMealInOrderWithUrl(url, order, request, isOrderForGroupParticipant),
      refreshedOrder => this.updateMealInOrderWithUrl(url, refreshedOrder, request, isOrderForGroupParticipant),
    );
  }

  async duplicateMeal(order: OrderWithContext, meal: OrderMeal): Promise<void> {
    if (meal.preconfiguredMealId && meal.preconfiguredMealName) {
      await this.addPreconfiguredMealToOrder(order, meal.preconfiguredMealId, meal.mealName);
    } else {
      await this.addMealToOrder(order, convertOrderMealToCreateMealRequest(meal));
    }
  }

  async duplicateRecentsAndFavsMeal(order: OrderWithContext, meal: RecentOrFavoriteOrderMeal): Promise<void> {
    await this.addMealToOrder(order, convertRecentAndFavsMealToCreateMealRequest(meal));
  }

  async copyMeals(fromId: string, to: OrderWithContext): Promise<void> {
    await this.performOrderOpAndRetryIfStale(
      to,
      () =>
        this.httpClient
          .post(`${ONLINE_ORDERS_BASE_ROUTE}/${to.order.orderId}/meals/copy/${fromId}`, {}, { headers: { 'If-Match': to.etag } })
          .then(async res => {
            updateOrderWithApiResponseEtag(to, res);
            await this.refreshOrder(to, true);
          })
          .catch(e => {
            if (e.response && e.response.status == 400) {
              generateOrderApiExceptionAndThrow(e);
            } else {
              this.logAndThrowApiError('copyMeals', e);
            }  
          }),
      refreshedOrder => this.copyMeals(fromId, refreshedOrder) as Promise<any>,
    );
  }

  removeDeliveryInfoFromOrder(order: OrderWithContext): Promise<any> {
    return this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .delete(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/delivery`, {
            headers: { 'If-Match': order.etag },
            params: { embeds: ORDER_EMBED, finalizePricing: true },
          })
          .then(res => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('removeDeliveryInfoFromOrder', e)),
      refreshedOrder => this.removeDeliveryInfoFromOrder(refreshedOrder),
    );
  }

  async addDeliveryInfoToOrder(order: OrderWithContext, deliveryInfo: UpdateDeliveryInfoOnOrderRequest): Promise<any> {
    return await this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .post(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/delivery`, deliveryInfo, {
            headers: { 'If-Match': order.etag },
            params: { embeds: ORDER_EMBED, finalizePricing: true },
          })
          .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('addDeliveryInfoToOrder', e)),
      refreshedOrder => this.addDeliveryInfoToOrder(refreshedOrder, deliveryInfo),
    );
  }

  updateDeliveryDropoffInfoOnOrder(order: OrderWithContext, deliveryInfo: UpdateDeliveryInfoOnOrderRequest): Promise<any> {
    return this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .put(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/delivery/dropoff`, deliveryInfo, {
            headers: { 'If-Match': order.etag },
            params: { embeds: ORDER_EMBED, finalizePricing: true },
          })
          .then(res => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('updateDeliveryDropoffInfoOnOrder', e))
          .catch((err: AxiosError) => generateOrderApiExceptionAndThrow(err)),
      refreshedOrder => this.updateDeliveryDropoffInfoOnOrder(refreshedOrder, deliveryInfo),
    );
  }

  async changeRestaurantForOrder(order: OrderWithContext, restaurantId: number, forPickup: boolean): Promise<any> {
    return await this.performOrderOpAndRetryIfStale(
      order,
      async () => {
        return await this.httpClient
          .put(
            `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/restaurant/${restaurantId}`,
            {},
            { headers: { 'If-Match': order.etag }, params: { embeds: ORDER_EMBED, finalizePricing: true } },
          )
          .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('changeOrderRestaurant', e))
          .catch((err: AxiosError) => {
            if (err.response && err.response.status == 400) {
              const errors = generateOrderApiErrors(err);

              const itemNotAvailableErrors = [
                OrderErrorCode.EntreeNotAvailable,
                OrderErrorCode.EntreeContentNotAvailable,
                OrderErrorCode.SideNotAvailable,
                OrderErrorCode.DrinkNotAvailable,
                OrderErrorCode.NonFoodItemNotAvailable,
              ];
              // Check if bag reconciliation needs to occur
              if (errors.every(e => itemNotAvailableErrors.includes(e.errorCode))) {
                this.analytics.send(
                  new ErrorEvent(
                    'fac',
                    `Sorry, some items in your bag aren't available at your new location. to switch to this location, remove the following and try again.`,
                    'bag-reconciliation',
                  ),
                );
                return new Promise((res, rej) => {
                  this.ui.setModalVisibility('bagReconciliation', true, {
                    restaurantId,
                    isPickup: forPickup,
                    itemsIds: errors.map(e => e.errorData),

                    // If the user declines to have the items removed, reject the promise so the initiator can recover
                    onCancel: () => rej(err),

                    // If the user does remove the items, try changing the restaurant again and then resolve the original request so the intiator can
                    // pick up up where they left off (they won't be aware that bag reconciliation even occurred)
                    onRemoveItems: () =>
                      this.changeRestaurantForOrder(order, restaurantId, forPickup)
                        .then(res)
                        .catch(() => rej(err)),
                  });
                  this.ui.firstFocus();
                });
              }

              generateOrderApiExceptionAndThrow(err);
            }

            return Promise.reject(err);
          });
      },
      refreshedOrder => this.changeRestaurantForOrder(refreshedOrder, restaurantId, forPickup),
    );
  }

  resetPendingOrders(): void {
    this.resetPendingOrder();
    this.resetPendingGroupOrder();
  }

  resetPendingOrder(): void {
    this.setUpsellMeal(null);
    this.setPendingOrder(null);
  }

  resetPendingGroupOrder(): void {
    this.setUpsellMeal(null);
    this.setPendingGroupOrder(null, '');
  }

  resetOrder(orderType: OrderType = OrderType.Regular) {
    if (orderType == OrderType.Regular) {
      this.resetPendingOrder();
    } else {
      this.resetPendingGroupOrder();
    }
  }

  setPendingGroupOrder(order: OrderWithContext | null, participantName: string): void {
    this.store.commit(MUTATIONS.SET_GROUP_ORDER, order);
    this.store.commit(MUTATIONS.SET_GROUP_ORDER_PARTICIPANT_NAME, participantName);
  }

  setPendingOrder(order: OrderWithContext | null): void {
    this.store.commit(MUTATIONS.SET_ORDER, order);
  }

  async refreshOrder(localOrder: OrderWithContext | null, finalizePricing: boolean): Promise<OrderWithContext | null> {
    if (!localOrder) {
      return null;
    }

    const order =
      localOrder.order.orderType == OrderType.Group && !this.isCustomerHostOfGroupOrder()
        ? await this.getGroupOrder(localOrder.order.groupOrderId, finalizePricing)
        : await this.getOrder(localOrder.order.orderId, finalizePricing);

    // we don't want to keep around completed orders on page load
    if (!order || order.order.orderStatus != OrderStatus.InProcess) {
      this.resetOrder(localOrder.order.orderType);
      return null;
    }

    await this.refreshDiscountsAndSetOrder(localOrder, order);
    return this.currentOrder;
  }

  async updateOrderTip(order: OrderWithContext, tip: number): Promise<any> {
    return this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .put(
            `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/delivery/tip`,
            { tip },
            { headers: { 'If-Match': order.etag }, params: { embeds: ORDER_EMBED, finalizePricing: true } },
          )
          .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('updateOrderTip', e)),
      refreshedOrder => this.updateOrderTip(refreshedOrder, tip),
    );
  }

  async updateOrderCrewTip(order: OrderWithContext, crewTip: number): Promise<any> {
    return this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .put(
            `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/tip`,
            { crewTip },
            { headers: { 'If-Match': order.etag }, params: { embeds: ORDER_EMBED, finalizePricing: true } },
          )
          .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('updateOrderCrewTip', e)),
      refreshedOrder => this.updateOrderCrewTip(refreshedOrder, crewTip),
    );
  }

  async applyDiscountToOrder(order: OrderWithContext, promotionCode: string): Promise<any> {
    this.setPromoCode(promotionCode);
    await this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .post(
            `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/promotions`,
            { promotionCode },
            { headers: { 'If-Match': order.etag }, params: { embeds: ORDER_EMBED, finalizePricing: true } },
          )
          .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('applyDiscountToOrder', e))
          .catch((e: AxiosError) => {
            if (e.response && e.response.status == 400) {
              throw new OrderApiException((e as AxiosError<OrderApiError[], any>).response.data);
            }
            throw e;
          }),
      refreshedOrder => this.applyDiscountToOrder(refreshedOrder, promotionCode),
    );
  }

  async removeDiscountFromOrder(order: OrderWithContext, promotionCode: string): Promise<any> {
    this.setPromoCode(promotionCode);
    await this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .delete(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/promotions/${promotionCode}`, {
            headers: { 'If-Match': order.etag },
            params: { embeds: ORDER_EMBED, finalizePricing: true },
          })
          .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
          .catch(e => this.logAndThrowApiError('removeDiscountFromOrder', e)),
      refreshedOrder => this.removeDiscountFromOrder(refreshedOrder, promotionCode),
    );
  }

  async updateMealName(order: OrderWithContext, mealId: string, mealName: string): Promise<any> {
    const url =
      order.order.orderType == OrderType.Group
        ? `${ONLINE_ORDERS_BASE_ROUTE}/group/${order.order.groupOrderId}/meals/${mealId}/name`
        : `${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/meals/${mealId}/name`;

    await this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .put(url, { mealName }, { headers: { 'If-Match': order.etag } })
          .then(res => updateOrderWithApiResponseEtag(order, res))
          .catch(e => this.logAndThrowApiError('updateMealName', e)),
      refreshedOrder => this.updateMealName(refreshedOrder, mealId, mealName),
    );

    await this.refreshOrder(order, true);
  }

  // changeCustomerOnOrder does not offer an Order embed
  async changeCustomerOnOrder(order: OrderWithContext, jwt: string): Promise<void> {
    return await this.performOrderOpAndRetryIfStale(
      order,
      async () =>
        await this.httpClient
          .put(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/customer`, { newCustomerJwt: jwt }, { headers: { 'If-Match': order.etag } })
          .then(res => updateOrderWithApiResponseEtag(order, res))
          .catch(e => this.logAndThrowApiError('changeCustomerOnOrder', e)),
      refreshedOrder => this.changeCustomerOnOrder(refreshedOrder, jwt) as Promise<any>,
    );
  }

  submitOrder(order: OrderWithContext, request: SubmitOrderRequest): Promise<void> {
    return this.performOrderOpAndRetryIfStale(
      order,
      () =>
        this.httpClient
          .post(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/submit`, request, { headers: { 'If-Match': order.etag } })
          .then(res => updateOrderWithApiResponseEtag(order, res))
          .catch(e => this.logAndThrowApiError('submitOrder', e))
          .catch((err: AxiosError) => generateOrderApiExceptionAndThrow(err)),
      refreshedOrder => this.submitOrder(refreshedOrder, request) as Promise<any>,
      undefined,
      undefined,
      2
    );
  }

  isCustomerHostOfGroupOrder(): boolean {
    return !!this.store.state.order.pendingGroupOrder && this.store.state.order.isGroupOrderHost;
  }

  isCustomerParticipantOfGroupOrder(): boolean {
    return this.store.state.order.isGroupOrderParticipant;
  }

  setIsCustomerHostOfGroupOrder(value: boolean) {
    this.store.commit(MUTATIONS.SET_IS_GROUP_ORDER_HOST, value);
  }

  clearGroupParticipantMeal() {
    this.store.commit(MUTATIONS.SET_GROUP_ORDER_MEAL_IDS, []);
  }

  groupParticpantHasMeal(mealIds: string[]): boolean {
    const order = this.store.state.order.pendingGroupOrder;
    return !order || !order.order.meals ? false : order.order.meals.filter(x => mealIds.indexOf(x.mealId) > -1).length >= 1;
  }

  setIsGroupOrderParticipant(isGroupOrderParticipant: boolean): void {
    this.store.commit(MUTATIONS.SET_IS_GROUP_ORDER_PARTICIPANT, isGroupOrderParticipant);
  }

  setLastOrder(order: OrderWithContext | null, estimate: DeliveryEstimate | null) {
    this.store.commit(MUTATIONS.SET_LAST_ORDER, { order, estimate });
  }

  async convertOrderToGroupOrder(order: OrderWithContext): Promise<OrderWithContext> {
    return await this.httpClient
      .put(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/ordertype/group`, null, {
        headers: {
          'If-Match': order.etag,
        },
      })
      .then((r: AxiosResponse<{ order: Order; }>) => ({ etag: r.headers['etag'], order: r.data.order, discounts: null }))
      .catch(e => this.logAndThrowApiError('convertOrderToGroupOrder', e));
  }

  async addMealToOrderWithUrl(url: string, order: OrderWithContext, request: OrderMealRequest, isOrderForGroupParticipant: boolean): Promise<string> {
    return await this.httpClient
      .post(url, request, {
        headers: {
          'If-Match': order.etag,
        },
        params: isOrderForGroupParticipant ? undefined : { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((res: AxiosResponse<{ order: Order; mealId: string; }>) => {
        this.handleOrderResponse(order, res.data.order, res);

        // The host doesn't need their meal id saved because they have full access to order
        if (isOrderForGroupParticipant) {
          this.sendJoinGroupOrderAnalyticsEvent(order);

          let groupOrderMealIds = this.store.state.order.groupOrderMealIds;
          groupOrderMealIds.push(res.data.mealId);

          this.store.commit(MUTATIONS.SET_GROUP_ORDER_MEAL_IDS, groupOrderMealIds);
        }

        return res.data.mealId;
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 400) {
          generateOrderApiExceptionAndThrow(err);
        }

        return Promise.reject(err);
      });
  }

  async updateCustomerLoyaltyEnrollmentStatus(order: OrderWithContext, jwt: string): Promise<void> {
    return await this.performOrderOpAndRetryIfStale(
      order,
      async () =>
        await this.httpClient
          .put(`${ONLINE_ORDERS_BASE_ROUTE}/${order.order.orderId}/customer`, { newCustomerJwt: jwt }, { headers: { 'If-Match': order.etag } })
          .then(res => updateOrderWithApiResponseEtag(order, res))
          .catch(e => this.logAndThrowApiError('updateCustomerLoyaltyEnrollmentStatus', e)),
      refreshedOrder => this.updateCustomerLoyaltyEnrollmentStatus(refreshedOrder, jwt) as Promise<any>,
    );
  }

  async addPreconfiguredMealToOrderWithUrl(
    url: string,
    order: OrderWithContext,
    preconfiguredMealId: string,
    mealName: string,
    isOrderForGroupParticipant: boolean,
  ): Promise<void> {
    return await this.httpClient
      .post(
        url,
        { preconfiguredMealId, mealName },
        {
          headers: {
            'If-Match': order.etag,
          },
          params: isOrderForGroupParticipant ? undefined : { embeds: ORDER_EMBED, finalizePricing: true },
        },
      )
      .then((res: AxiosResponse<{ mealId: string; order: Order; }>) => {
        this.handleOrderResponse(order, res.data.order, res);
        // The host doesn't need their meal id saved because they have full access to order
        if (isOrderForGroupParticipant) {
          this.sendJoinGroupOrderAnalyticsEvent(order);

          let groupOrderMealIds = this.store.state.order.groupOrderMealIds;
          groupOrderMealIds.push(res.data.mealId);

          this.store.commit(MUTATIONS.SET_GROUP_ORDER_MEAL_IDS, groupOrderMealIds);
        }
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 400) {
          generateOrderApiExceptionAndThrow(err);
        }

        return Promise.reject(err);
      });
  }

  async addNonFoodItemToOrderWithUrl(url: string, order: OrderWithContext, request: NonFoodItemRequest, isOrderForGroupParticipant: boolean) {
    return await this.httpClient
      .post(url, request, {
        headers: {
          'If-Match': order.etag,
        },
        params: isOrderForGroupParticipant ? undefined : { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((res: AxiosResponse<{ nonFoodItemId: string; order: Order; }>) => this.handleOrderResponse(order, res.data.order, res))
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 400) {
          generateOrderApiExceptionAndThrow(err);
        }

        return Promise.reject(err);
      });
  }

  // Currently there's no way in the UI to edit a meal to have a different entree type. This case is unhandled.
  async updateMealInOrderWithUrl(url: string, order: OrderWithContext, request: OrderMealRequest, isOrderForGroupParticipant: boolean): Promise<void> {
    return await this.httpClient
      .put(url, request, {
        headers: {
          'If-Match': order.etag,
        },
        params: isOrderForGroupParticipant ? undefined : { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((res: AxiosResponse<{ order: Order; }>) => this.handleOrderResponse(order, res.data.order, res))
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 400) {
          generateOrderApiExceptionAndThrow(err);
        }

        return Promise.reject(err);
      });
  }

  async removeMealFromOrderWithUrl(url: string, order: OrderWithContext, isOrderForGroupParticipant: boolean) {
    return await this.httpClient
      .delete(url, {
        headers: {
          'If-Match': order.etag,
        },
        params: isOrderForGroupParticipant ? undefined : { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
      .catch(e => this.logAndThrowApiError('removeMealFromOrderWithUrl', e));
  }

  async removeNonFoodItemFromOrderWithUrl(url: string, order: OrderWithContext, isOrderForGroupParticipant: boolean) {
    return await this.httpClient
      .delete(url, {
        headers: {
          'If-Match': order.etag,
        },
        params: isOrderForGroupParticipant ? undefined : { embeds: ORDER_EMBED, finalizePricing: true },
      })
      .then((res: AxiosResponse<Order>) => this.handleOrderResponse(order, res.data, res))
      .catch(e => this.logAndThrowApiError('removeNonFoodItemFromOrderWithUrl', e));
  }

  async removeItemsFromOrder(order: OrderWithContext, items: UndeliverableItem[]) {
    const invalidItemsLookup = arrayToMap(items, i => i.id);

    const getValidContents = (contents: OrderMealEntreeContents[]): OrderMealEntreeContents[] => {
      return contents.filter(c => !invalidItemsLookup[c.menuItemId]);
    };

    const getValidEntrees = (entrees: OrderMealEntree[]): [OrderMealEntree[], boolean] => {
      const validEntrees = entrees.filter(e => !invalidItemsLookup[e.menuItemId]);
      return [validEntrees, validEntrees.length == entrees.length];
    };

    const getValidItems = (mealItems: OrderMealItem[]): [OrderMealItem[], boolean] => {
      const validItems = mealItems.filter(i => !invalidItemsLookup[i.menuItemId]);
      return [validItems, validItems.length == mealItems.length];
    };

    const invalidNonFoodItems = (order.order.nonFoodItems || []).filter(i => invalidItemsLookup[i.menuItemId]);

    await Promise.all(invalidNonFoodItems.map(nonFoodItem => this.removeNonFoodItemFromOrder(order, nonFoodItem.nonFoodItemId)));

    for (const meal of order.order.meals || []) {
      const [validEntrees, entreesValid] = getValidEntrees(meal.entrees || []);
      const [validSides, sidesValid] = getValidItems(meal.sides || []);
      const [validDrinks, drinksValid] = getValidItems(meal.drinks || []);

      let invalidContent = false;
      for (const entree of validEntrees) {
        if (!entree.contents) {
          continue;
        }
        const contentCount = entree.contents.length;
        entree.contents = getValidContents(entree.contents);
        if (contentCount != entree.contents.length) {
          invalidContent = true;
        }
      }

      if (!entreesValid || !sidesValid || !drinksValid || invalidContent) {
        // If there are no items left, just remove the meal
        if (!validEntrees.length && !validSides.length && !validDrinks.length) {
          await this.removeMealFromOrder(order, meal.mealId);
          continue;
        }

        // Otherwise, update the meal to only include the valid items
        await this.updateMealInOrder(order, meal.mealId, {
          meal: {
            mealName: meal.mealName,
            entrees: validEntrees.map(e => ({
              ...e,
              contents: getValidContents(e.contents || []),
            })),
            sides: validSides,
            drinks: validDrinks,
          },
        });
      }
    }
  }

  getDeliveryEstimate(request: GetDeliveryEstimateRequest): Promise<DeliveryEstimate> {
    return this.httpClient
      .post(`${DELIVERIES_BASE_ROUTE}/estimate`, request)
      .then((r: AxiosResponse<DeliveryEstimate>) => r.data)
      .catch(e => this.logAndThrowApiError('getDeliveryEstimate', e));
  }

  async validateDeliveryAddress(address: DeliveryAddressValidationRequest) {
    return await this.httpClient
      .post(`${DELIVERIES_BASE_ROUTE}/estimate`, address)
      .then((r: any) => r)
      .catch((e: AxiosError) => {
        if (e?.response?.data && e.response.status == 400) {
          const apiError = e as AxiosError<ApiValidationError, any>;
          let validationErrors: DeliveryApiError[] = [
            {
              errorCode: DeliveryErrorCode[apiError.response.data.errorCode],
              errorData: null,
              errorDescription: apiError.response.data.message,
            },
          ];

          if (apiError.response.data.validationErrors) {
            const apiValidationErrors = apiError.response.data.validationErrors.map((error: DeliveryApiValidationError) => ({
              errorCode: error.errorCode,
              errorData: null,
              errorDescription: error.message,
            }));
            validationErrors = validationErrors.concat(apiValidationErrors);
          }
          return new DeliveryApiException(validationErrors);
        }
        this.logAndThrowApiError('validateDeliveryAddress', e);
      });
  }

  setUpsellMeal(meal: OrderMeal | null): void {
    this.store.commit(MUTATIONS.SET_UPSELL_MEAL, meal);
  }

  setPromoCode(promoCode: string) {
    this.store.commit(MUTATIONS.SET_PROMO_CODE, promoCode, { root: true });
  }

  setPromoErrorMessage(promoErrorMessage: string) {
    this.store.commit(MUTATIONS.SET_PROMO_ERROR_MESSAGE, promoErrorMessage, { root: true });
  }

  private async performOrderOpAndRetryIfStale<T>(
    order: OrderWithContext,
    op: () => Promise<T>,
    retryOp: (order: OrderWithContext) => Promise<T>,
    groupOrderId?: string,
    depth?: number,
    maxDepth?: number,
  ): Promise<T> {
    if (!depth) {
      depth = 0;
    }

    if (!maxDepth) {
      maxDepth = NUM_MAX_ORDER_OP_RETRIES;
    }

    if (depth > maxDepth) {
      throw new Error('Max num retries exceeded');
    }

    try {
      return await op();
    } catch (e) {
      if (this.isAxiosErrorWithStatusCode(e, 412) && depth < maxDepth) {
        const { orderId, orderType } = order.order;

        let refreshedOrder: OrderWithContext | null;
        if (orderType == OrderType.Group && !this.isCustomerHostOfGroupOrder()) {
          refreshedOrder = await this.getGroupOrder(order.order.groupOrderId);
        } else {
          refreshedOrder = await this.getOrder(orderId);
        }

        if (!refreshedOrder) {
          throw Error(`Order not found: ${orderId}`);
        }

        return await this.performOrderOpAndRetryIfStale(
          refreshedOrder,
          async () => await retryOp(refreshedOrder!),
          async () => await retryOp(refreshedOrder!),
          groupOrderId,
          depth + 1,
          maxDepth,
        );
      }

      if (this.isAxiosErrorWithStatusCode(e, 404)) {
        throw new OrderNotFoundException();
      }

      // Refresh order if there's a non-2XX/412 as a failsafe to avoid etag conflicts in the event that an update failed partway through on COP's end, changing the resource
      await this.refreshCurrentOrder();

      return this.logAndThrowApiError('performOrderOpAndRetryIfStale', e);
    }
  }

  private async getOrderData(url: string, finalizePricing: boolean): Promise<OrderWithContext | null> {
    return this.httpClient
      .get(`${url}?finalizePricing=${finalizePricing}`)
      .then((r: AxiosResponse<{ order: Order; }>) => ({ etag: r.headers['etag'], order: r.data.order, discounts: null }))
      .catch(e => {
        if (this.isAxiosErrorWithStatusCode(e, 404)) {
          return null;
        }

        return this.logAndThrowApiError('getOrderData', e);
      });
  }

  private sendJoinGroupOrderAnalyticsEvent(order: OrderWithContext): void {
    this.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.Behavior,
      action: AnalyticsEventAction.Click,
      label: AnalyticsEventLabel.JoinGroupOrder,
      values: { customEventGroupOrderId: order.order.groupOrderId },
    });
  }

  private async handleOrderResponse(originalOrder: OrderWithContext, updatedOrder: Order | null, response: AxiosResponse<any>) {
    const orderWithContext: OrderWithContext = {
      etag: response.headers['etag'],
      order: updatedOrder?.orderId ? updatedOrder : originalOrder.order,
      discounts: originalOrder.discounts ?? null,
    };

    await this.refreshDiscountsAndSetOrder(originalOrder, orderWithContext);
  }

  async processOrderDiscountErrors(order: OrderWithContext, promoCode: string) {
    let header = '';
    let content = '';

    this.setPromoCode(promoCode);
    this.setPromoErrorMessage('');

    switch (order.order.discountErrorCode) {
      case OrderErrorCode.PromotionPickUpOnly:
        this.setPromoErrorMessage('Promo code is for pickup only');
        if (this.store.state.ui.bag.visible || this.checkingOut) {
          if (this.isDelivery()) {
            this.showPickUpOnlyPromoErrorModal();
          }
        }
        break;
      case OrderErrorCode.PromotionDeliveryOnly:
        if (this.store.state.ui.bag.visible || this.checkingOut) {
          this.showDeliveryOnlyPromoErrorModal();
        }
        break;
      case OrderErrorCode.PromotionCodeInvalidLocation:
        header = 'Oh Bummer.';
        content = 'This promo code isn\'t valid at this location.';
        break;
      case OrderErrorCode.PromotionCodeInvalidAvailability:
        header = 'Oh Bummer.';
        content = 'Looks like this promo code is only valid ' + order.order.discountErrorContent + '.';
        break;
      case OrderErrorCode.PromotionCodeAlreadyRedeemed:
        header = 'Oh Bummer.';
        content = 'Looks like this promo code is used up and no longer valid.';
        break;
      case OrderErrorCode.PromotionCodeInvalidMinimumAmount:
        header = 'Something\'s Not Right.';
        content = 'Looks like the minimum order amount hasn\'t been met.';
        break;
      case OrderErrorCode.PromotionCodeHasExpired:
        promoCode = 'Promo code has expired';
        break; 
      case OrderErrorCode.PromotionCodeRewardsMembersOnly: 
          if (!this.customer || this.isLoyaltyEligible()) { 
            this.removeDiscountFromOrder(order, promoCode); 
            this.setPromoCode('');
            this.ui.setModalVisibility('rewardsMembersOnlyModal', true); 
          } else { 
            header = 'Oh Bummer.'; 
            content = 'This promo code isn\'t valid at this location.'; 
          } 
          break; 
    }

    if (header != '') {
      this.removeDiscountFromOrder(order, promoCode);
      this.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: 'Okay',
            type: ButtonType.Primary,
            onClick: () => {
              this.ui.setModalVisibility('basicError', false);
            },
          },
        ],
      });
    }
  }

  makeFACPromoOptions(mode: 'pickup' | 'delivery'): FACPromoOptions {

    const savedAddress = this.store.state.order.cachedDeliveryAddress;
    const savedRestaurant = this.cachedRestaurant || this.store.state.restaurant.selectedRestaurant;
    return mode === 'pickup' ? {
      mode, pickupSearchInput: savedRestaurant?.address.addressLine1,
    } : {
        mode, deliverySearchInput: savedAddress?.streetAddress1
      };
  }

  async findAChipotleDeliveryModal(mode: 'pickup' | 'delivery') {

    this.ui.setModalVisibility('findAChipotle', true, {
      ...this.makeFACPromoOptions(mode),
      onClose: () => {
        const promoCode = this.currentOrder?.discounts![0].promotionCode || this.store.state.order.promoCode;
        if (this.store.state.order.promoCode && this.checkingOut) {
          this.applyDiscountToOrder(this.currentOrder, promoCode);
        }
        if(this.store.state.order.promoErrorMessage && this.isPickingUp()){
          this.setPromoErrorMessage('');
        }
      }
    });

    const modeSelect = `${mode}-select`;
    this.ui.setLastSelectedElement(modeSelect);
    this.ui.setModalVisibility('basicError', false);
  }

  async removeAllDiscounts() {
    if (this.currentOrder) {
      for (const discount of this.currentOrder.discounts!) {
        await this.removeDiscountFromOrder(this.currentOrder, discount.promotionCode);
      }
    }
  }

  checkPromoAlreadyApplied(promoCode: string) {
    return this.currentOrder.discounts?.find(d => d.promotionCode === promoCode) || this.currentOrder.order.discounts?.find(d => d.discountCode === promoCode);
  }

  async revertBackToCheckOutPickUp() {
    this.switchToPickup();
    if (!this.currentOrder.discounts?.length) {
      const promoCode = this.store.state.order.promoCode;
      if (promoCode && this.isPickingUp() && !this.checkPromoAlreadyApplied(promoCode)) {
        await this.applyDiscountToOrder(this.currentOrder, promoCode);
      }
    }

    const savedRestaurant = this.store.state.restaurant.selectedRestaurant || this.cachedRestaurant;
    if (savedRestaurant && this.isDelivery()) {
      this.switchToPickup();
      this.setCachedRestaurant(savedRestaurant);
    }

    await this.refreshCurrentOrder();
    this.ui.setAppPepperVisibility(false);
    this.ui.setModalVisibility('basicError', false);
  }

  async revertBackToCheckOutDelivery() {
    if (!this.currentOrder.discounts?.length) {
      const promoCode = this.store.state.order.promoCode;
      if (promoCode) {
        this.ui.setAppPepperVisibility(true);
        await this.applyDiscountToOrder(this.currentOrder, promoCode);
        this.ui.setAppPepperVisibility(false);
      }
    }

    const savedAddress = this.store.state.order.cachedDeliveryAddress;
    if (savedAddress) {
      await this.setDeliveryAddress(savedAddress);
    }
    await this.refreshCurrentOrder();
  }

  makePromoModalOptions({ errorType, firstButton, firstOnClick, secondButton, secondOnClick, ...args }: PromoErrorParams) {
    return {
      ...args,
      options: [{
        text: firstButton,
        type: ButtonType.Primary,
        onClick: firstOnClick
      }, {
        text: secondButton,
        type: ButtonType.Secondary,
        onClick: secondOnClick || args.onClose
      }]
    };

  }

  genericPromoErrorModal(options: PromoErrorParams) {
    this.ui.setModalVisibility('basicError', true, this.makePromoModalOptions(options));
  }

  async onClosingModal(type: PromoOnlyErrorTypes) {
    this.ui.setAppPepperVisibility(true);

    if (this.checkingOut) {
      switch (type) {
        case PromoOnlyErrorTypes.DELIVERY_ONLY: await this.revertBackToCheckOutDelivery();
          break;
        case PromoOnlyErrorTypes.PICKUP_ONLY: if (this.isDelivery()) {
          this.setPromoErrorMessage('Promo code is for pickup only.')
          await this.revertBackToCheckOutPickUp();
        }
          break;
      }
    } else {
      await this.removeAllDiscounts();
      this.ui.setModalVisibility('findAChipotle', false);
      this.ui.firstFocus();
    }
    this.ui.setAppPepperVisibility(false);
    this.ui.setModalVisibility('basicError', false);
  }

  showPickUpOnlyPromoErrorModal() {
    const firstButton = 'Switch To Pickup';
    const secondButton = this.checkingOut ? 'Close' : 'Back To Bag';
    const content = this.checkingOut ? 'Switching to delivery will remove this promo from your order' : 'Switch to pickup to use this promo code.';
    this.genericPromoErrorModal({
      errorType: PromoOnlyErrorTypes.PICKUP_ONLY,
      header: 'Aww shucks, your promo code is for pickup only',
      content,
      firstButton,
      secondButton,
      firstOnClick: async () => {
        switch (firstButton) {
          case 'Switch To Pickup': {
            if (!this.checkingOut) {
              this.ui.setModalVisibility('basicError', false);
              this.findAChipotleDeliveryModal('pickup');
              this.bag.setBagVisibility(false);
            } else {
              if (!this.isDelivery()) {
                this.findAChipotleDeliveryModal('delivery');
              }
              await this.revertBackToCheckOutPickUp();
              this.ui.setModalVisibility('basicError', false);
            }
            break;
          }
        }
      },
        secondOnClick: async () => {
        await this.removeAllDiscounts();
        this.ui.setModalVisibility('findAChipotle', false);
        this.ui.firstFocus();
          this.ui.setModalVisibility('basicError', false);
          this.setPromoErrorMessage('Promo code is for pickup only.');
      },
      onClose: async () => {
        await this.removeAllDiscounts();
        this.ui.setModalVisibility('basicError', false);
        this.setPromoErrorMessage('Promo code is for pickup only.');
      },
    });
  }

  showDeliveryOnlyPromoErrorModal() {
    const firstButton = this.checkingOut ? 'Switch To Pickup' : 'Switch To Delivery';
    const secondButton = this.checkingOut ? 'Close' : 'Back to Bag';
    const content = this.checkingOut ? 'Switching to pickup will remove this promo from your order.' : 'Switch to delivery to use the promo code.';
    this.genericPromoErrorModal({
      errorType: PromoOnlyErrorTypes.DELIVERY_ONLY,
      header: 'Aww shucks, your promo code is for delivery only',
      content,
      firstButton,
      secondButton,
      firstOnClick: async () => {
        switch (firstButton) {
          case 'Switch To Delivery': {
            this.ui.setModalVisibility('basicError', false);
            this.findAChipotleDeliveryModal('delivery');
            this.bag.setBagVisibility(false);
            break;
          }
          case 'Switch To Pickup': {
            if (!this.isPickingUp()) {
              this.findAChipotleDeliveryModal('pickup');
            }

            this.removeAllDiscounts();
            this.ui.setModalVisibility('basicError', false);
            break;
          }
        }
      },
      onClose: async () => await this.onClosingModal(PromoOnlyErrorTypes.DELIVERY_ONLY)
    });
  }

  get checkingOut() {
    const check = router.currentRoute.name === Routes.Checkout;
    return check;
  }

  private async refreshDiscountsAndSetOrder(localOrder: OrderWithContext | null, updatedOrder: OrderWithContext) {
    if (!isEqual(updatedOrder.order.discounts, localOrder?.order.discounts) || (updatedOrder.order.discounts && updatedOrder.discounts == null)) {
      updatedOrder.discounts = (await Promise.all((updatedOrder.order.discounts ?? []).map(d => this.promotions.getPromotionDetails(d.discountCode)))) as Promotion[];
    }

    this.processOrderDiscountErrors(updatedOrder, this.store.state.order.promoCode);

    if (updatedOrder.order.orderType == OrderType.Group) {
      this.setPendingGroupOrder(updatedOrder, this.store.state.order.groupOrderParticpantName);
    } else {
      this.setPendingOrder(updatedOrder);
    }
  }

  setLastRetrievalTypeInStore(type: string): void {
    this.store.commit(MUTATIONS.SET_LAST_RETRIEVAL_TYPE, type);
  }
}
